import { useNavigate } from 'react-router-dom';

import MeetingCard from '@lib/core/src/components/MeetingCard/MeetingCard';
import palette from '@lib/core/src/theme/palette';
import { Skeleton, Stack, Typography } from '@mui/material';

import { CalendarMeetingInfoDto } from '@/api/generated';
import { useSelectedMeetingsActions, useIsMeetingSelected } from '@/store/selectedMeetingsStore';

import { MeetingHubEmptyState } from './MeetingHubEmptyState';
import { onMeetingClick } from './utils/util';

export const MeetingHubContent: React.FC<{
  isLoading: boolean;
  isEmptyGroupedMeetings: boolean;
  summary: string;
  initFilters: () => void;
  setMeetingHoverId: React.Dispatch<React.SetStateAction<string>>;
  meetingLoading: boolean;
  meetingHoverId: string;
  dayGroups: {
    dayKey: string;
    dayTimestamp: number;
    meetings: CalendarMeetingInfoDto[];
  }[];
}> = ({
  isLoading,
  isEmptyGroupedMeetings,
  dayGroups,
  initFilters,
  setMeetingHoverId,
  summary,
  meetingHoverId,
  meetingLoading
}) => {
  const navigate = useNavigate();
  const { addRemoveSelectedMeeting } = useSelectedMeetingsActions();

  const handleCheckboxClick = (meeting: CalendarMeetingInfoDto) => {
    addRemoveSelectedMeeting(meeting);
  };

  if (isLoading) {
    return (
      <>
        <Skeleton width={210} height={40} />
        <Stack flexDirection={'row'} flexWrap={'wrap'} gap={2}>
          {Array.from({ length: 10 }, (_, i) => (
            <Skeleton variant="rounded" key={i} width={194} sx={{ height: 196 }} />
          ))}
        </Stack>
      </>
    );
  }
  if (isEmptyGroupedMeetings) return <MeetingHubEmptyState initFilters={initFilters} />;

  return dayGroups?.map(dayGroup => (
    <Stack key={dayGroup.dayKey} gap={'5px'} marginBottom={'20px'}>
      <Typography sx={{ fontSize: '11px', color: palette.grey[500] }}>{dayGroup.dayKey}</Typography>
      <Stack flexDirection={'row'} flexWrap={'wrap'} gap={2}>
        {dayGroup.meetings.map(meeting => (
          <CardWrapper
            key={meeting.id}
            meeting={meeting}
            setMeetingHoverId={setMeetingHoverId}
            onMeetingClick={() => onMeetingClick(meeting, navigate)}
            summary={summary}
            meetingHoverId={meetingHoverId}
            meetingLoading={meetingLoading}
            handleCheckboxClick={handleCheckboxClick}
          />
        ))}
      </Stack>
    </Stack>
  ));
};

const CardWrapper = ({
  meeting,
  setMeetingHoverId,
  onMeetingClick,
  summary,
  meetingHoverId,
  meetingLoading,
  handleCheckboxClick
}: {
  meeting: CalendarMeetingInfoDto;
  setMeetingHoverId: (id: string) => void;
  onMeetingClick: (meeting: CalendarMeetingInfoDto) => void;
  summary: string;
  meetingHoverId: string;
  meetingLoading: boolean;
  handleCheckboxClick: (meeting: CalendarMeetingInfoDto) => void;
}) => {
  const isMeetingSelected = useIsMeetingSelected(meeting.id || '');
  return (
    <MeetingCard
      key={meeting.id}
      meeting={meeting}
      onSummaryHover={id => setMeetingHoverId(id)}
      onClick={() => onMeetingClick(meeting)}
      summary={meetingHoverId === meeting.id ? summary : undefined}
      isLoadingSummary={meetingHoverId === meeting.id && meetingLoading}
      isChecked={isMeetingSelected}
      onCheckboxClick={handleCheckboxClick}
    />
  );
};
